/**
 * PlatformShell — Enterprise Mint Teal shell for Super Admin routes.
 * ADR-WF-1 v2.1 §26 · Platform Nav.
 * Rebrand v3 (Feb 2026) · Midnight sidebar · Mint active · Soft Ice text.
 */
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Building2, ShieldCheck, UserCog, Activity, Heart, LogOut, ChevronLeft, ChevronRight } from "lucide-react";
import { ShellFrame } from "@/components/layout/ShellFrame";
import { SideNavItem, withTooltip } from "@/components/layout/SideNavItem";
import { TooltipProvider } from "@/components/ui/tooltip";
import { HocpMark } from "@/components/BrandLogo";

import { useAuth } from "@/contexts/AuthContext";

const NAV = [
  { to: "/platform", label: "Dashboard", icon: LayoutDashboard, testid: "platform-nav-dashboard", end: true },
  { to: "/platform/hotels", label: "Hotels", icon: Building2, testid: "platform-nav-hotels" },
  { to: "/platform/events", label: "Events", icon: Activity, testid: "platform-nav-events" },
  { to: "/platform/system-health", label: "System Health", icon: Heart, testid: "platform-nav-system-health" },
  { to: "/platform/admins", label: "Super Admins", icon: ShieldCheck, testid: "platform-nav-admins" },
  { to: "/platform/account", label: "My Account", icon: UserCog, testid: "platform-nav-account" },
];

export default function PlatformShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = async () => { await logout(); navigate("/login"); };

  const renderSidebar = ({ collapsed, mobile, close, toggle }) => (
    <TooltipProvider>
      <div className={`h-16 flex items-center border-b border-hocp-deepTeal shrink-0 ${collapsed ? "justify-center" : "px-4 gap-3"}`} data-testid="platform-sidebar">
        {collapsed ? (
          <button type="button" onClick={toggle} className="w-8 h-8 rounded-lg bg-hocp-mint text-hocp-midnight font-display font-bold text-[16px] flex items-center justify-center" aria-label="Expand sidebar" data-testid="platform-brand-mini">H</button>
        ) : (
          <>
            <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center shadow-sm shrink-0"><HocpMark size={30} /></div>
            <div className="leading-none">
              <div className="font-display font-bold tracking-tight text-[18px] text-hocp-softIce">HOCP</div>
              <div className="text-[10px] mt-1 tracking-[0.20em] uppercase text-hocp-mint font-semibold">Platform</div>
            </div>
            {!mobile && <button type="button" onClick={toggle} className="ml-auto w-9 h-9 flex items-center justify-center rounded-lg text-hocp-softIce hover:bg-hocp-deepTeal" aria-label="Collapse sidebar" data-testid="platform-sidebar-collapse"><ChevronLeft size={20} strokeWidth={1.5} /></button>}
          </>
        )}
      </div>
      {collapsed && <button type="button" onClick={toggle} className="mx-auto mt-2 w-9 h-9 flex items-center justify-center rounded-lg text-hocp-softIce hover:bg-hocp-deepTeal" aria-label="Expand sidebar" data-testid="platform-sidebar-expand"><ChevronRight size={20} strokeWidth={1.5} /></button>}
      <nav className={`flex-1 overflow-y-auto py-4 space-y-0.5 ${collapsed ? "px-1" : "px-2"}`}>
        {NAV.map((it) => <SideNavItem key={it.to} {...it} collapsed={collapsed} onNavigate={close} />)}
      </nav>
      <div className={`border-t border-hocp-deepTeal shrink-0 ${collapsed ? "p-1" : "p-2"}`}>
        {!collapsed && (
          <div className="px-3 py-2 rounded-lg mb-2 bg-hocp-deepTeal">
            <div className="text-[11px] uppercase tracking-wider text-hocp-mint font-semibold">Super Admin</div>
            <div className="text-[13px] font-medium text-hocp-softIce truncate" data-testid="platform-current-user">{user?.email}</div>
          </div>
        )}
        {withTooltip(collapsed, "Log out", (
          <button data-testid="platform-logout" onClick={handleLogout} aria-label="Log out"
            className={`w-full flex items-center gap-3 min-h-[44px] rounded-lg text-[13px] text-hocp-softIce/70 hover:bg-hocp-deepTeal hover:text-white transition-colors ${collapsed ? "justify-center" : "px-3"}`}>
            <LogOut size={20} strokeWidth={1.5} />{!collapsed && "Log out"}
          </button>
        ))}
      </div>
    </TooltipProvider>
  );

  return (
    <ShellFrame testid="platform-shell" renderSidebar={renderSidebar} topbar={null}
      onBell={() => navigate("/platform/events")} onUser={() => navigate("/platform/account")} contentClassName="max-w-[1400px]">
      <Outlet />
    </ShellFrame>
  );
}
